import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Camera } from 'lucide-react'; 
import api from '../../utils/axios'; 
import * as tf from '@tensorflow/tfjs';

// Import the new CSS file
import './WasteClassifier.css'; 

// Color map for our waste types
const OTHER_WASTE_TYPE_STYLES = {
  "Hazardous (Batteries/Chemicals)": { backgroundColor: '#e74c3c' }, // Red
  "E-Waste (Electronics)": { backgroundColor: '#9b59b6' }, // Purple
  "Glass": { backgroundColor: '#3498db' }, // Blue
  "Metal": { backgroundColor: '#95a5a6' }, // Silver
  "Paper/Cardboard": { backgroundColor: '#e67e22' }, // Orange
  "Plastic": { backgroundColor: '#f1c40f', color: '#333' }, // Yellow
  "Medical Waste": { backgroundColor: '#c0392b' }, // Dark Red
};

const WasteClassifier = ({ onClose }) => {
  // --- STATE & REFS ---
  const [imageURL, setImageURL] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [result, setResult] = useState(null); 
  const [confidence, setConfidence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCorrectionUI, setShowCorrectionUI] = useState(false);
  const [tfModel, setTfModel] = useState(null);
  const [modelLoading, setModelLoading] = useState(true);
  
  const uploadInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const imageElementRef = useRef(null); // Ref for the image to be processed

  // --- CONFIGURATION ---
  const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
  const UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;
  // Fetch from the React public folder directly, bypassing the python server
  const MODEL_URL = '/tfjs_graph_model/model.json?v=4';
  const INDEXEDDB_URL = 'indexeddb://waste-model';

  const binMap = {
    'Organic': 'Green Bin 🌿',
    'Recyclable': 'Blue Bin ♻️',
  };

  const OTHER_WASTE_TYPES = Object.keys(OTHER_WASTE_TYPE_STYLES);

  // --- 1. LOAD MODEL ON MOUNT ---
  useEffect(() => {
    const loadModel = async () => {
      setModelLoading(true);
      try {
        // First try to load from IndexedDB cache
        let loadedModel;
        try {
          loadedModel = await tf.loadGraphModel(INDEXEDDB_URL);
          console.log('✅ Graph Model loaded successfully from IndexedDB cache');
        } catch (cacheError) {
          console.log('Graph Model not in cache, fetching from network...');
          // If not in cache, fetch from the Python static server
          loadedModel = await tf.loadGraphModel(MODEL_URL);
          // Save it to cache for next time
          await loadedModel.save(INDEXEDDB_URL);
          console.log('✅ Graph Model fetched and cached in IndexedDB');
        }
        setTfModel(loadedModel);
      } catch (error) {
        console.error('❌ Failed to load TFJS model:', error);
      } finally {
        setModelLoading(false);
      }
    };
    
    // Warm up tfjs engine
    tf.ready().then(() => {
      loadModel();
    });
  }, []);

  // --- 2. HANDLE UPLOAD ---
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImageURL(url);
      setImageFile(file);
      setResult(null);
      setConfidence(null);
      setShowCorrectionUI(false);
    }
  };

  // --- 3. PREDICT (IN-BROWSER OFFLINE) ---
  const classifyImage = async () => {
    if (!imageElementRef.current || !tfModel) return;
    setLoading(true);
    setShowCorrectionUI(false);

    try {
      // Run preprocessing and inference within tf.tidy to automatically clean up tensors
      const score = tf.tidy(() => {
        // 1. Process image: extract pixels
        let tensor = tf.browser.fromPixels(imageElementRef.current);
        
        // 2. Resize to 224x224 (matching the Python backend's PIL resize)
        tensor = tf.image.resizeBilinear(tensor, [224, 224]);
        
        // 3. Normalize pixels from [0, 255] to [-1, 1] 
        // Python: (img_array / 127.5) - 1.0
        const offset = tf.scalar(127.5);
        const normalized = tensor.div(offset).sub(tf.scalar(1.0));
        
        // 4. Expand dims to shape [1, 224, 224, 3] for a batch of 1
        const batched = normalized.expandDims(0);
        
        // 5. Predict
        const prediction = tfModel.predict(batched);
        
        // 6. Get raw score
        return prediction.dataSync()[0];
      });

      console.log('Offline Prediction Score:', score);

      // Interpret score (identical to python backend thresholding)
      if (score > 0.2 && score < 0.8) setResult("UNKNOWN");
      else if (score <= 0.2) setResult("Organic");
      else setResult("Recyclable");

      setConfidence(score > 0.5 ? score * 100 : (1 - score) * 100);
    } catch (error) {
      console.error("In-browser prediction error:", error);
      alert("Offline Prediction failed. See console for details.");
    }
    
    setLoading(false);
  };

  // --- 4. CLOUDINARY UPLOAD ---
  const uploadToCloudinary = async (finalLabel) => {
    if (!CLOUD_NAME || !UPLOAD_PRESET) {
        alert("Cloudinary credentials are not set in .env file.");
        return false; 
    }
    setLoading(true);
    if (!imageFile) {
        console.error("No file selected for upload.");
        setLoading(false);
        return false; 
    }

    const formData = new FormData();
    formData.append("file", imageFile); 
    formData.append("upload_preset", UPLOAD_PRESET);
    const cleanTag = finalLabel.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, '');
    formData.append("tags", `verified_${cleanTag}, waste_app_v1`);

    try {
      await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
        method: "POST",
        body: formData,
      });
      alert(`Thank you! Image saved as "${finalLabel}".`);
      setShowCorrectionUI(false);
      return true; 
    } catch (error) {
      console.error(error);
      alert("Upload failed. Check console.");
      return false; 
    } finally {
      setLoading(false);
    }
  };

  // --- UI HELPERS ---
  const handleCorrect = async () => { 
    const success = await uploadToCloudinary(result); 
    if (success) onClose(); 
  };
  
  const handleIncorrect = () => { 
    setShowCorrectionUI(true); 
  };

  const handleOtherChoice = async (type) => {
    const success = await uploadToCloudinary(type);
    if (success) onClose(); 
  };

  return (
    // We use a wrapper class for modal-specific styles
    <div className="wc-container">
      <button onClick={onClose} className="wc-close-button">
        <X size={24} />
      </button>

      <h2 className="wc-header">♻️ Quick Identify</h2>
      
      {modelLoading && (
        <div className="wc-loading-banner" style={{ textAlign: 'center', padding: '10px', backgroundColor: '#f39c12', color: 'white', borderRadius: '4px', marginBottom: '15px' }}>
          Downloading Offline AI Model... (Once loaded, it works without internet!)
        </div>
      )}

      {!imageFile && (
        <div className="wc-input-choice-box">
          <button 
            className="wc-choice-button" 
            onClick={() => uploadInputRef.current.click()}
            disabled={modelLoading}
          >
            <Upload size={18} /> Upload Photo
          </button>
          <button 
            className="wc-choice-button" 
            onClick={() => cameraInputRef.current.click()}
            disabled={modelLoading}
          >
             <Camera size={18} /> Take Photo
          </button>

          {/* Hidden Inputs */}
          <input type="file" accept="image/*" onChange={handleImageUpload} ref={uploadInputRef} style={{ display: 'none' }} />
          <input type="file" accept="image/*" capture="environment" onChange={handleImageUpload} ref={cameraInputRef} style={{ display: 'none' }} />
        </div>
      )}
      
      {imageURL && (
        <div className="wc-preview-container">
          <img 
            src={imageURL} 
            alt="Waste" 
            className="wc-image" 
            ref={imageElementRef}
            crossOrigin="anonymous" 
          />
          <br />
          {!result && (
             <button onClick={classifyImage} disabled={loading || modelLoading} className="wc-button-primary">
               {loading ? "Analyzing..." : (modelLoading ? "Model Loading..." : "🔍 Identify Waste (Offline)")}
             </button>
          )}
        </div>
      )}

      {/* --- RESULT AREA --- */}
      {result && !loading && !showCorrectionUI && (
        <div className="wc-result-box">
          
          {result === 'UNKNOWN' ? (
            <div className="wc-unknown-box">
              <h4 style={{margin: '5px 0'}}>I can't identify this correctly.</h4>
              <p style={{fontSize: '14px', margin: '10px 0'}}>I am trained on a small dataset. Please help us learn by identifying the type of waste:</p>
              
              <div className="wc-grid">
                {OTHER_WASTE_TYPES.map((type) => (
                  <button 
                    key={type} 
                    onClick={() => handleOtherChoice(type)}
                    className="wc-other-button"
                    style={OTHER_WASTE_TYPE_STYLES[type]} // Apply dynamic colors
                  >
                    {type}
                  </button>
                ))}
              </div>
              
              <button onClick={onClose} className="wc-skip-button wc-skip-button-full">Skip</button>
            </div>
          ) : (
            <div>
              <h3>I think this is: <span className={result === 'Organic' ? 'wc-text-organic' : 'wc-text-recyclable'}>{result}</span></h3>
              <p>Confidence: {confidence.toFixed(2)}%</p>
              
              <div className="wc-recommendation-box">
                <span style={{fontSize: '1.2em'}}>Recommened Bin:</span>
                <h3 style={{marginTop: '5px'}}>{binMap[result]}</h3>
              </div>
              
              <p>Is this correct?</p>
              <div className="wc-button-group">
                <button onClick={handleCorrect} className="wc-yes-button">✅ Yes</button>
                <button onClick={handleIncorrect} className="wc-no-button">❌ No</button>
                <button onClick={onClose} className="wc-skip-button">Skip</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- CORRECTION UI --- */}
      {showCorrectionUI && (
        <div className="wc-correction-box">
          <h4>Help us learn! What is it actually?</h4>
          {result !== 'UNKNOWN' && (
            <button 
              onClick={async () => { 
                const success = await uploadToCloudinary(result === 'Organic' ? 'Recyclable' : 'Organic');
                if (success) onClose();
              }} 
              className="wc-option-button"
            >
              Actually, it IS {result === 'Organic' ? 'Recyclable ♻️' : 'Organic 🌿'}
            </button>
          )}
          <p style={{margin: '10px 0'}}>OR select another type:</p>
          <div className="wc-grid">
            {OTHER_WASTE_TYPES.map((type) => (
              <button 
                key={type} 
                onClick={() => handleOtherChoice(type)} 
                className="wc-other-button"
                style={OTHER_WASTE_TYPE_STYLES[type]}
              >
                {type}
              </button>
            ))}
          </div>
          <button onClick={() => setShowCorrectionUI(false)} className="wc-cancel-button">Cancel</button>
        </div>
      )}
    </div>
  );
};

export default WasteClassifier;